import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Space, Table, Tag, Typography, theme as antdTheme } from 'antd';
import type { TableProps } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { AsyncBoundary, EM_DASH } from '@viq-admin/ui';
import { fetchTelemetryInstalls, type DeviceInstall } from '../../api/telemetry';
import { timeAgo } from './timeAgo';
import { When } from './When';

/**
 * This user's mobile app installs and each one's sync state, so "jane says
 * the app won't sync" is answered on her page: which phone, which app
 * version, when it last opened, last synced, last failed and why, and
 * whether leads are stuck on it.
 *
 * Source: GET /v1/superadmin/telemetry/installs?companyId=<the user's
 * tenant>, filtered here to `install.oid === oid`. The endpoint has no per-
 * user filter; each install row carries the oid and company id the server
 * stamped from the verified token and `user_profiles` (never from the
 * device), and a user never changes tenant, so the user's company holds all
 * of their installs. The company is passed explicitly, so the section shows
 * the same rows whatever "Operate as" is set to. `read User` gates the
 * endpoint, which every console role has. (The endpoint caps a response at
 * the 2000 most recently seen installs; a tenant would need far more devices
 * than any has today before an old install of this user's fell off.)
 *
 * The Stranded and Outdated flags come from the server, by the same rules as
 * the Telemetry page. Stranded rows are tinted with antd's error background
 * and failing ones with its warning background (theme tokens, so both
 * themes work).
 *
 * Field reps use the app; admins and stakeholders often never do, so an
 * empty list is normal and says so rather than looking like a failure.
 */

const PLATFORM_LABEL: Record<string, string> = { ios: 'iOS', android: 'Android' };

function deviceLabel(i: DeviceInstall): string {
  if (!i.platform) return EM_DASH;
  const platform = PLATFORM_LABEL[i.platform] ?? i.platform;
  return i.osVersion && i.osVersion !== 'unknown' ? `${platform} ${i.osVersion}` : platform;
}

type Health = 'stranded' | 'failing' | 'ok';

function healthOf(i: DeviceInstall): Health {
  if (i.stranded) return 'stranded';
  if (i.consecutiveSyncFailures > 0) return 'failing';
  return 'ok';
}

function StatusTags({ install: i }: { install: DeviceInstall }) {
  const failing = i.consecutiveSyncFailures > 0;
  return (
    <Space size={4} wrap>
      {i.stranded && (
        <Tag
          color="error"
          icon={<WarningOutlined />}
          title="Unsynced rows on the device and no successful sync in 24 hours"
        >
          Stranded
        </Tag>
      )}
      {failing && (
        <Tag color="warning" icon={<CloseCircleOutlined />} title="The latest sync attempts failed">
          Failing
        </Tag>
      )}
      {!i.stranded && !failing && (
        <Tag color="success" icon={<CheckCircleOutlined />} title="Syncing">
          OK
        </Tag>
      )}
    </Space>
  );
}

const columns: NonNullable<TableProps<DeviceInstall>['columns']> = [
  {
    key: 'status',
    title: 'Status',
    render: (_: unknown, i: DeviceInstall) => <StatusTags install={i} />,
  },
  { key: 'device', title: 'Device', render: (_: unknown, i: DeviceInstall) => deviceLabel(i) },
  {
    key: 'version',
    title: 'App version',
    render: (_: unknown, i: DeviceInstall) =>
      i.appVersion ? (
        <Space size={4}>
          <span>
            {i.appVersion}
            {i.build ? ` (${i.build})` : ''}
          </span>
          {i.outdated && (
            <Tag icon={<ClockCircleOutlined />} title="Older than the newest version in the fleet">
              Outdated
            </Tag>
          )}
        </Space>
      ) : (
        EM_DASH
      ),
  },
  {
    key: 'lastOpenedAt',
    title: 'Last opened',
    render: (_: unknown, i: DeviceInstall) => <When iso={i.lastOpenedAt} stacked />,
  },
  {
    key: 'lastSeenAt',
    title: 'Last seen',
    render: (_: unknown, i: DeviceInstall) => <When iso={i.lastSeenAt} stacked />,
  },
  {
    key: 'lastSyncOkAt',
    title: 'Last synced',
    render: (_: unknown, i: DeviceInstall) => <When iso={i.lastSyncOkAt} empty="Never" stacked />,
  },
  {
    key: 'lastSyncFailAt',
    title: 'Last failed sync',
    render: (_: unknown, i: DeviceInstall) => {
      const detail = [
        i.lastSyncErrorClass,
        i.consecutiveSyncFailures > 0 ? `${i.consecutiveSyncFailures} in a row` : null,
      ]
        .filter(Boolean)
        .join(' · ');
      return (
        <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
          <When iso={i.lastSyncFailAt} empty="None" />
          {detail && <Typography.Text type="secondary">{detail}</Typography.Text>}
        </span>
      );
    },
  },
  {
    key: 'unsynced',
    title: 'Unsynced rows',
    render: (_: unknown, i: DeviceInstall) =>
      i.unsyncedCount === null ? (
        EM_DASH
      ) : i.unsyncedCount > 0 ? (
        <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
          <span>{i.unsyncedCount}</span>
          {i.oldestUnsyncedAt && (
            <Typography.Text type="secondary">oldest {timeAgo(i.oldestUnsyncedAt)}</Typography.Text>
          )}
        </span>
      ) : (
        0
      ),
  },
];

export function UserDevices({ oid, companyId }: { oid: string; companyId: string }) {
  const { token } = antdTheme.useToken();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['userDevices', companyId],
    queryFn: () => fetchTelemetryInstalls({ companyId }),
  });
  // Newest-seen first, as the endpoint orders them.
  const installs = useMemo(
    () => (data?.installs ?? []).filter((i) => i.oid === oid),
    [data, oid],
  );

  const tint: Record<Health, string | undefined> = {
    stranded: token.colorErrorBg,
    failing: token.colorWarningBg,
    ok: undefined,
  };

  return (
    <AsyncBoundary
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
      empty={!isLoading && !error && installs.length === 0}
      emptyTitle="No app installs reported"
      emptyDescription="An install appears once this user signs in to a version of the mobile app that reports telemetry. Users who only use the web portal have none."
    >
      <Table<DeviceInstall>
        rowKey="installId"
        size="small"
        dataSource={installs}
        columns={columns}
        pagination={false}
        scroll={{ x: 'max-content' }}
        onRow={(i) => {
          const health = healthOf(i);
          return {
            'data-health': health,
            style: tint[health] ? { background: tint[health] } : undefined,
          } as React.HTMLAttributes<HTMLTableRowElement>;
        }}
      />
    </AsyncBoundary>
  );
}
